import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiGallection } from '@wix/da-papi-types';
import {
  GridBreakpoint,
  GridElement,
  StandardGrid,
} from '@wix/da-shared-react/pkg/Grid';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ShopItemType } from '../../../../../types/shop';
import ShopThumb, {
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import ShopFolderThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/ShopFolder';
import CommissionThumbWithMenu from '../_partials/CommissionThumbWithMenu';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadableDuperbrowseContext from '../../../../contexts/LoadableDuperbrowseContext';
import EmptyState from '../_partials/EmptyState';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import CommissionShopCard from '@wix/da-shared-react/pkg/ShopCard/CommissionShopCard';
import PCPShopCard from '@wix/da-shared-react/pkg/ShopCard/PCPShopCard';
import FolderShopCard from '@wix/da-shared-react/pkg/ShopCard/FolderShopCard';
import AdoptableShopCard from '@wix/da-shared-react/pkg/ShopCard/AdoptableShopCard';

import s from './ShopGrid.scss';

const CARD_PANEL_HEIGHT = 89;

export const SHOP_CARDS_BREAKPOINTS: GridBreakpoint[] = [
  {
    maxWidth: 735,
    elementsPerRow: 1,
    elementHeight: 275,
  },
  {
    maxWidth: 1280,
    elementsPerRow: 3,
    elementHeight: 275,
  },
  {
    maxWidth: 1536,
    elementsPerRow: 4,
    elementHeight: 275,
  },
  {
    maxWidth: 1920,
    elementsPerRow: 5,
    elementHeight: 275,
  },
  {
    maxWidth: 9999,
    elementsPerRow: 6,
    elementHeight: 275,
  },
];

export const itemTypeToThumbTypeMap = {
  [ShopItemType.PREMIUM_CONTENT]: ShopThumbType.PCP,
  [ShopItemType.COMMISSIONS]: ShopThumbType.COMMISSION,
};

const itemTypeToBiWidgetMap = {
  [ShopItemType.PREMIUM_CONTENT]: 'downloads',
  [ShopItemType.COMMISSIONS]: 'commissions',
};

export interface Props {
  streamId: string;
  className?: string;
  items: PapiGallection[] | PapiDeviation[];
  itemType: ShopItemType;
  hasMore: boolean;
  isFetching: boolean;
  fetchMore: () => void;
  emptyState?: React.ReactNode;
  uploadMore?: React.ReactNode;
  isOwner: boolean;
}

export const ShopGrid: React.FC<Props> = ({
  streamId,
  items,
  itemType,
  hasMore,
  isFetching,
  fetchMore,
  emptyState,
  uploadMore,
  isOwner,
}) => {
  const { t } = useTranslation();
  const is_shop_card = useFlag('shop_card');

  const renderElement = useCallback(
    (element: GridElement) => {
      const { index, width, height, style } = element;

      let thumb;

      if (is_shop_card) {
        if (itemType === ShopItemType.COMMISSIONS) {
          thumb = (
            <CommissionShopCard
              key={index}
              deviation={items[index] as PapiDeviation}
              width={width}
              withAuthorDetails={false}
              withIndicators={false}
            />
          );
        } else if (itemType === ShopItemType.PREMIUM_CONTENT) {
          thumb = (
            <PCPShopCard
              key={index}
              deviation={items[index] as PapiDeviation}
              width={width}
              withAuthorDetails={false}
              withIndicators={false}
            />
          );
        } else if (itemType === ShopItemType.PREMIUM_FOLDERS) {
          thumb = (
            <FolderShopCard
              key={index}
              folder={items[index] as PapiGallection}
              width={width}
              withAuthorDetails={false}
              withIndicators={false}
            />
          );
        } else if (itemType === ShopItemType.ADOPTABLES) {
          thumb = (
            <AdoptableShopCard
              key={index}
              deviation={items[index] as PapiDeviation}
              width={width}
              withAuthorDetails={false}
              withIndicators={false}
            />
          );
        }
        return <div style={{ ...style, height: 'auto' }}>{thumb}</div>;
      }

      switch (itemType) {
        case ShopItemType.COMMISSIONS:
          thumb = (
            <CommissionThumbWithMenu
              key={index}
              deviation={items[index] as PapiDeviation}
              width={width}
              height={height}
            />
          );
          break;
        case ShopItemType.PREMIUM_FOLDERS:
          thumb = (
            <ShopFolderThumb
              key={index}
              folder={items[index] as PapiGallection}
              width={width}
              height={height}
            />
          );
          break;
        default:
          thumb = (
            <ShopThumb
              key={index}
              deviation={items[index] as PapiDeviation}
              width={width}
              height={height}
              type={itemTypeToThumbTypeMap[itemType]}
              showAuthor={false}
            />
          );
      }

      return <div style={{ ...style }}>{thumb}</div>;
    },
    [itemType, items]
  );

  const getItemId = useCallback(
    (index: number) => {
      if (itemType === ShopItemType.PREMIUM_FOLDERS) {
        const item = items[index] as PapiGallection;
        return `${index}-${item.folderId}`;
      } else {
        const item = items[index] as PapiDeviation;
        return `${index}-${item.deviationId}`;
      }
    },
    [itemType, items]
  );

  if (!items.length) {
    if (emptyState === false) {
      return null;
    }
    if (emptyState) {
      return <>{emptyState}</>;
    }

    return <EmptyState view="large" shopItemType={itemType} />;
  }

  return (
    <ErrorBoundary debugComponent="ShopGrid">
      <LoadableDuperbrowseContext
        streamId={streamId}
        parent={{ title: t('duperbrowse.backButton.profile.shop') }}
      >
        <BiLoggerContextProvider
          value={{ widgetname: itemTypeToBiWidgetMap[itemType] }}
        >
          {isOwner && <div className={s['upload-more']}>{uploadMore}</div>}
          <StandardGrid
            elementCount={items.length}
            cookieType={MeasuredCookieType.USER_PROFILE_SHOP}
            getElementId={getItemId}
            adjustHeight={is_shop_card ? undefined : CARD_PANEL_HEIGHT}
            breakpoints={is_shop_card ? SHOP_CARDS_BREAKPOINTS : undefined}
            preserveAspectRatio={is_shop_card ? false : true}
            enableScrollOptim
          >
            {renderElement}
          </StandardGrid>
        </BiLoggerContextProvider>
      </LoadableDuperbrowseContext>
      {isFetching && <LoadingIndicator />}
      <OnScrollBottom
        distanceToBottom={2}
        distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
        onScrolledToBottom={hasMore && !isFetching ? fetchMore : undefined}
      />
    </ErrorBoundary>
  );
};
ShopGrid.displayName = 'ShopGrid';

export default ShopGrid;
